import React, { useEffect, useState } from "react";
import { CreditCard, CheckCircle2, XCircle, Loader2, RefreshCw, Copy } from "lucide-react";
import { toast } from "sonner";
import api, { BACKEND_URL } from "@/lib/api";

const STATUS = {
  default: ["Using dashboard default", "bg-slate-100 text-slate-600"],
  connected: ["Connected", "bg-emerald-100 text-emerald-700"],
  failed: ["Connection failed", "bg-rose-100 text-rose-700"],
  not_tested: ["Not checked yet", "bg-amber-100 text-amber-800"],
};

// Per-form payment gateway: dashboard default Razorpay, or this form's own Razorpay account.
export const FormGatewaySettings = ({ formId }) => {
  const [gw, setGw] = useState(null);
  const [form, setForm] = useState({ mode: "default", key_id: "", key_secret: "", webhook_secret: "", account_label: "" });
  const [busy, setBusy] = useState("");
  const [recent, setRecent] = useState(null);

  useEffect(() => {
    if (!formId) return;
    api.get(`/forms/${formId}/payment-gateway`).then((r) => {
      setGw(r.data);
      setForm((f) => ({ ...f, mode: r.data.mode, key_id: r.data.key_id, account_label: r.data.account_label }));
    }).catch(() => {});
  }, [formId]);

  if (!formId) return <div className="rounded-xl border border-dashed border-slate-200 p-4 text-xs text-slate-500" data-testid="form-gateway-unsaved">Save the form first to choose its payment gateway.</div>;
  if (!gw) return null;

  const save = async () => {
    setBusy("save");
    try {
      const r = await api.put(`/forms/${formId}/payment-gateway`, form);
      setGw(r.data); setForm((f) => ({ ...f, key_secret: "", webhook_secret: "" }));
      toast.success(form.mode === "custom" ? "Gateway saved — run Check status to confirm it works" : "Form now uses the dashboard's default Razorpay");
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    setBusy("");
  };
  const test = async () => {
    setBusy("test");
    try { const r = await api.post(`/forms/${formId}/payment-gateway/test`); setGw(r.data); setRecent(r.data.recent_payments); toast[r.data.status === "connected" ? "success" : "error"](r.data.status_message); }
    catch (e) { toast.error(e?.response?.data?.detail || "Check failed"); }
    setBusy("");
  };
  const [label, cls] = STATUS[gw.status] || STATUS.not_tested;
  const webhook = `${BACKEND_URL}${gw.webhook_path}`;

  return (
    <div className="rounded-xl border border-violet-200 bg-violet-50/40 p-4" data-testid="form-gateway-settings">
      <div className="flex items-center gap-2 mb-3">
        <CreditCard className="w-4 h-4 text-violet-600" />
        <div className="font-semibold text-slate-900 text-sm flex-1">Payment gateway</div>
        <span className={`text-[10px] font-bold rounded-full px-2 py-0.5 ${cls}`} data-testid="form-gateway-status">{label}</span>
      </div>
      <div className="grid grid-cols-2 gap-2 mb-3">
        {[["default", "Dashboard default (Razorpay)"], ["custom", "Connect another Razorpay account"]].map(([m, l]) => (
          <button key={m} type="button" onClick={() => setForm({ ...form, mode: m })} data-testid={`form-gateway-mode-${m}`}
            className={`text-xs font-semibold rounded-lg px-3 py-2 border ${form.mode === m ? "border-violet-500 bg-white text-violet-700" : "border-slate-200 text-slate-600"}`}>{l}</button>
        ))}
      </div>
      {form.mode === "custom" && (
        <div className="space-y-2 mb-3">
          <input className="input-pill w-full" placeholder="Account name (e.g. School's own account)" value={form.account_label} onChange={(e) => setForm({ ...form, account_label: e.target.value })} data-testid="form-gateway-label" />
          <input className="input-pill w-full font-mono text-xs" placeholder="Key ID (rzp_live_… or rzp_test_…)" value={form.key_id} onChange={(e) => setForm({ ...form, key_id: e.target.value })} data-testid="form-gateway-key-id" />
          <input type="password" className="input-pill w-full font-mono text-xs" placeholder={gw.key_secret_masked ? `Key Secret (saved: ${gw.key_secret_masked}) — leave blank to keep` : "Key Secret"} value={form.key_secret} onChange={(e) => setForm({ ...form, key_secret: e.target.value })} data-testid="form-gateway-key-secret" />
          <input type="password" className="input-pill w-full font-mono text-xs" placeholder={gw.webhook_secret_set ? "Webhook secret (saved) — leave blank to keep" : "Webhook secret (optional, recommended)"} value={form.webhook_secret} onChange={(e) => setForm({ ...form, webhook_secret: e.target.value })} data-testid="form-gateway-webhook-secret" />
          <div className="text-[11px] text-slate-500 flex items-center gap-1 flex-wrap">In that Razorpay account → Settings → Webhooks, add <code className="bg-white px-1 rounded">{webhook}</code>
            <button type="button" onClick={() => { navigator.clipboard.writeText(webhook); toast.success("Copied"); }} className="text-violet-600" data-testid="form-gateway-copy-webhook"><Copy className="w-3 h-3" /></button>
            with events payment.captured, payment.failed, order.paid.</div>
        </div>
      )}
      <div className="flex gap-2">
        <button type="button" onClick={save} disabled={!!busy} className="btn-primary text-xs py-1.5" data-testid="form-gateway-save">{busy === "save" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null} Save gateway</button>
        <button type="button" onClick={test} disabled={!!busy} className="btn-ghost text-xs py-1.5" data-testid="form-gateway-test">{busy === "test" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />} Check status</button>
      </div>
      {gw.status_message && <div className={`text-[11px] mt-2 flex items-center gap-1 ${gw.status === "connected" ? "text-emerald-700" : gw.status === "failed" ? "text-rose-700" : "text-slate-500"}`} data-testid="form-gateway-message">{gw.status === "connected" ? <CheckCircle2 className="w-3 h-3" /> : gw.status === "failed" ? <XCircle className="w-3 h-3" /> : null}{gw.status_message}{gw.checked_at && ` · ${new Date(gw.checked_at).toLocaleString()}`}</div>}
      {recent && (
        <div className="mt-3 bg-white rounded-lg border border-slate-100 divide-y divide-slate-50" data-testid="form-gateway-recent">
          <div className="px-3 py-1.5 text-[10px] font-bold uppercase text-slate-500">Latest payments on this account</div>
          {recent.length === 0 ? <div className="px-3 py-2 text-xs text-slate-400">No payments yet.</div> : recent.map((p) => (
            <div key={p.id} className="px-3 py-1.5 text-xs flex gap-2"><span className="font-mono text-slate-500">{p.id}</span><span className="flex-1 truncate">{p.email}</span><span>₹{(p.amount_paise || 0) / 100}</span><span className={`font-bold ${p.status === "captured" ? "text-emerald-600" : p.status === "failed" ? "text-rose-600" : "text-amber-600"}`}>{p.status}</span></div>
          ))}
        </div>
      )}
    </div>
  );
};
